import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as ImportIcon,
  Print as PrintIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import api from '../../utils/api';
import QuoteDialog from './QuoteDialog';
import QuotePrint from './QuotePrint';
import jsPDF from 'jspdf';

const QuoteViewDialog = ({ 
  open, 
  onClose, 
  onImport, 
  maintenanceId,
  customerId,
  vehicleId,
  date
}) => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [quotesReloadTrigger, setQuotesReloadTrigger] = useState(0);

  const fetchQuotes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      let response;
      if (maintenanceId) {
        // Se abbiamo un maintenanceId, cerca per quello
        response = await api.get(`/quotes/maintenance/${maintenanceId}`);
      } else if (customerId && vehicleId) {
        // Per nuove manutenzioni, cerca solo preventivi standalone (senza maintenanceId)
        response = await api.get(`/quotes?customerId=${customerId}&vehicleId=${vehicleId}&standalone=true`);
      } else {
        setQuotes([]);
        return;
      }
      
      setQuotes(response.data);
    } catch (err) {
      console.error('Errore nel caricamento preventivi:', err);
      setError('Errore nel caricamento dei preventivi');
    } finally {
      setLoading(false);
    }
  }, [maintenanceId, customerId, vehicleId, date]);

  useEffect(() => {
    if (open) {
      fetchQuotes();
    }
  }, [open, quotesReloadTrigger, fetchQuotes]);

  const generateQuotePdf = async (quotePrintData) => {
    try {
      // Recupera i dati aziendali
      const companyId = localStorage.getItem('companyId');
      const companySettingsResponse = await api.get('/company-settings', {
        headers: { 'x-company-id': companyId }
      });
      const companyData = companySettingsResponse.data;

      const { quote, customer, vehicle, items, totals, maintenanceId: quoteMaintenanceId } = quotePrintData;
      const currentDate = new Date();
      
      // Recupera il numero di manutenzione se disponibile
      let protocolNumber = null;
      const maintId = quote?.maintenanceId || quoteMaintenanceId;
      if (maintId) {
        try {
          const maintenanceResponse = await api.get(`/maintenance/${maintId}`);
          protocolNumber = maintenanceResponse.data?.protocolNumber;
        } catch (err) {
          console.error('Errore nel recupero numero manutenzione:', err);
        }
      }
      
      // Dimensioni A5
      const pageWidth = 148;
      const pageHeight = 210;
      const margin = 10;
      const contentWidth = pageWidth - (margin * 2);
      
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [pageWidth, pageHeight]
      });

      // Funzione per aggiungere l'intestazione su ogni pagina
      const addHeader = (pdf, companyData, quoteNumber, protocolNum, pageNum) => {
        // Nome azienda (più compatto)
        pdf.setFontSize(11);
        pdf.setTextColor(219, 17, 17);
        pdf.setFont(undefined, 'bold');
        pdf.text(companyData?.companyName || 'FreeWheel', margin, margin);
        
        // Dati azienda (più compatti)
        pdf.setFontSize(7);
        pdf.setTextColor(80, 80, 80);
        pdf.setFont(undefined, 'normal');
        let yPos = margin + 4;
        
        const addressLine = companyData?.companyAddress ? 
          `${companyData.companyAddress}, ${[companyData?.companyPostalCode, companyData?.companyCity].filter(Boolean).join(' ')}${companyData?.companyProvince ? ` (${companyData.companyProvince})` : ''}` : 
          [companyData?.companyPostalCode, companyData?.companyCity].filter(Boolean).join(' ') + (companyData?.companyProvince ? ` (${companyData.companyProvince})` : '');
        
        if (addressLine) {
          pdf.text(addressLine, margin, yPos);
          yPos += 3;
        }
        
        if (companyData?.companyPhone) {
          pdf.text(`Tel: ${companyData.companyPhone}`, margin, yPos);
          yPos += 3;
        }
        
        // Numero preventivo e manutenzione in alto a destra
        pdf.setFontSize(6);
        pdf.setTextColor(100, 100, 100);
        pdf.text('Preventivo N.:', pageWidth - margin - 25, margin);
        pdf.setFontSize(9);
        pdf.setTextColor(219, 17, 17);
        pdf.setFont(undefined, 'bold');
        pdf.text(quoteNumber || 'N/A', pageWidth - margin - 25, margin + 3);
        
        if (protocolNum) {
          pdf.setFontSize(6);
          pdf.setTextColor(100, 100, 100);
          pdf.text('Manutenzione N.:', pageWidth - margin - 25, margin + 7);
          pdf.setFontSize(9);
          pdf.setTextColor(219, 17, 17);
          pdf.setFont(undefined, 'bold');
          pdf.text(String(protocolNum), pageWidth - margin - 25, margin + 10);
        }
        
        // Linea separatrice (più in alto)
        pdf.setDrawColor(219, 17, 17);
        pdf.setLineWidth(0.3);
        pdf.line(margin, margin + 15, pageWidth - margin, margin + 15);
      };

      // Funzione per aggiungere il footer
      const addFooter = (pdf, pageNum, totalPages) => {
        const footerY = pageHeight - 8;
        pdf.setFontSize(7);
        pdf.setTextColor(120, 120, 120);
        pdf.setFont(undefined, 'normal');
        pdf.text(`Pagina ${pageNum} di ${totalPages}`, pageWidth / 2, footerY, { align: 'center' });
      };

      let currentY = margin + 18;
      const lineHeight = 4;
      const sectionSpacing = 4;
      let pageNum = 1;
      const footerHeight = 10;
      const minSpaceForItem = 15; // Spazio minimo necessario per un item

      // Aggiungi intestazione prima pagina
      addHeader(pdf, companyData, quote?.quoteNumber, protocolNumber, pageNum);

      // Titolo documento (centrato correttamente)
      pdf.setFontSize(14);
      pdf.setTextColor(219, 17, 17);
      pdf.setFont(undefined, 'bold');
      const titleY = currentY + 4; // Spazio sopra il titolo
      pdf.text('PREVENTIVO', pageWidth / 2, titleY, { align: 'center' });
      currentY = titleY + 6; // Spazio sotto il titolo prima della linea

      // Linea sotto il titolo
      pdf.setDrawColor(219, 17, 17);
      pdf.setLineWidth(0.4);
      pdf.line(margin, currentY, pageWidth - margin, currentY);
      currentY += 4;

      // Info Cliente e Veicolo (più compatti, su due colonne)
      pdf.setFontSize(7);
      pdf.setTextColor(60, 60, 60);
      pdf.setFont(undefined, 'bold');
      
      // Cliente a sinistra
      pdf.text('Cliente:', margin, currentY);
      pdf.setFont(undefined, 'normal');
      pdf.text(`${customer?.firstName || ''} ${customer?.lastName || ''}`, margin + 15, currentY);
      currentY += 3;
      
      if (customer?.phone) {
        pdf.text(`Tel: ${customer.phone}`, margin + 15, currentY);
        currentY += 3;
      }
      
      // Veicolo a destra
      if (vehicle) {
        pdf.setFont(undefined, 'bold');
        pdf.text('Veicolo:', margin + 70, currentY - (customer?.phone ? 6 : 3));
        pdf.setFont(undefined, 'normal');
        const vehicleInfo = [vehicle.brand, vehicle.model].filter(Boolean).join(' ');
        if (vehicleInfo) {
          pdf.text(vehicleInfo, margin + 85, currentY - (customer?.phone ? 6 : 3));
        }
      }
      
      // Data preventivo
      pdf.setFontSize(7);
      pdf.text(`Data: ${format(currentDate, 'dd/MM/yyyy', { locale: it })}`, pageWidth - margin, currentY - 3, { align: 'right' });
      
      currentY += 4;
      pdf.setDrawColor(220, 220, 220);
      pdf.setLineWidth(0.2);
      pdf.line(margin, currentY, pageWidth - margin, currentY);
      currentY += 4;

      // Descrizione lavori (più compatta)
      if (quote?.description) {
        pdf.setFontSize(8);
        pdf.setFont(undefined, 'bold');
        pdf.text('Descrizione:', margin, currentY);
        currentY += 3;
        
        pdf.setFontSize(7);
        pdf.setFont(undefined, 'normal');
        const descriptionLines = pdf.splitTextToSize(quote.description, contentWidth);
        descriptionLines.forEach(line => {
          if (currentY > pageHeight - footerHeight - minSpaceForItem) {
            addFooter(pdf, pageNum, 0);
            pdf.addPage();
            pageNum++;
            addHeader(pdf, companyData, quote?.quoteNumber, protocolNumber, pageNum);
            currentY = margin + 18;
          }
          pdf.text(line, margin, currentY);
          currentY += 3;
        });
        
        currentY += 3;
      }

      // Tabella prodotti
      pdf.setFontSize(8);
      pdf.setFont(undefined, 'bold');
      pdf.text('DETTAGLIO PREVENTIVO', margin, currentY);
      currentY += 4;

      // Intestazione tabella (colonne meglio allineate)
      pdf.setFontSize(6);
      pdf.setFont(undefined, 'bold');
      pdf.setTextColor(255, 255, 255);
      pdf.setFillColor(219, 17, 17);
      pdf.rect(margin, currentY - 3, contentWidth, 5, 'F');
      
      // Colonne: Codice (20mm), Descrizione (60mm), Q.tà (8mm), Prezzo (12mm), Sconto (12mm), Totale (12mm)
      pdf.text('Codice', margin + 1, currentY);
      pdf.text('Descrizione', margin + 22, currentY);
      pdf.text('Q.tà', margin + 83, currentY, { align: 'center' });
      pdf.text('Prezzo', margin + 93, currentY, { align: 'right' });
      pdf.text('Sconto', margin + 106, currentY, { align: 'right' });
      pdf.text('Totale', margin + 126, currentY, { align: 'right' });
      
      currentY += 5;
      pdf.setDrawColor(200, 200, 200);
      pdf.setLineWidth(0.2);
      pdf.line(margin, currentY, pageWidth - margin, currentY);
      currentY += 2;

      // Righe prodotti
      pdf.setFontSize(6.5);
      pdf.setTextColor(0, 0, 0);
      pdf.setFont(undefined, 'normal');
      
      // Funzione helper per aggiungere intestazione tabella su nuova pagina
      const addTableHeader = (pdf, yPos) => {
        pdf.setFontSize(6);
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(255, 255, 255);
        pdf.setFillColor(219, 17, 17);
        pdf.rect(margin, yPos - 3, contentWidth, 5, 'F');
        pdf.text('Codice', margin + 1, yPos);
        pdf.text('Descrizione', margin + 22, yPos);
        pdf.text('Q.tà', margin + 83, yPos, { align: 'center' });
        pdf.text('Prezzo', margin + 93, yPos, { align: 'right' });
        pdf.text('Sconto', margin + 106, yPos, { align: 'right' });
        pdf.text('Totale', margin + 126, yPos, { align: 'right' });
        const headerEndY = yPos + 5;
        pdf.setDrawColor(200, 200, 200);
        pdf.line(margin, headerEndY, pageWidth - margin, headerEndY);
        return headerEndY + 2;
      };
      
      // Assicurati che items sia un array
      const itemsArray = Array.isArray(items) ? items : [];
      
      // Processa tutti gli item
      for (let index = 0; index < itemsArray.length; index++) {
        const item = itemsArray[index];
        const itemTotal = (item.quantity || 0) * parseFloat(item.unitPrice || 0);
        const discountAmount = parseFloat(item.discount) || 0;
        const finalTotal = itemTotal - discountAmount;
        
        // Mostra solo il nome prodotto, non la descrizione
        const productName = item.productName || '';
        
        // Calcola l'altezza necessaria per questo item (solo nome, max 60mm)
        const descLines = pdf.splitTextToSize(productName, 60); // 60mm per la descrizione
        const itemHeight = Math.max(4, descLines.length * 3) + 3; // +3 per spazio extra
        
        // Controlla se serve una nuova pagina PRIMA di disegnare l'item
        if (currentY + itemHeight > pageHeight - footerHeight - 2) {
          addFooter(pdf, pageNum, 0);
          pdf.addPage();
          pageNum++;
          addHeader(pdf, companyData, quote?.quoteNumber, protocolNumber, pageNum);
          currentY = addTableHeader(pdf, margin + 18);
          pdf.setFontSize(6.5);
          pdf.setFont(undefined, 'normal');
        }

        // Salva la Y iniziale e la pagina iniziale per questo item (per codice e valori)
        const itemStartY = currentY;
        const itemStartPage = pageNum;
        const lineSpacing = 3.5; // Spaziatura costante tra le righe
        
        // Codice (allargato a 20mm) - solo sulla prima riga
        const codeText = item.productCode || '-';
        pdf.text(codeText, margin + 1, itemStartY);
        
        // Descrizione (solo nome prodotto, max 60mm, può essere su più righe)
        // Usa un'altezza di riga costante per mantenere l'allineamento
        let descCurrentY = itemStartY;
        let descCurrentPage = itemStartPage;
        
        descLines.forEach((line, idx) => {
          if (idx === 0) {
            // Prima riga della descrizione
            pdf.text(line, margin + 22, descCurrentY);
          } else {
            // Calcola la posizione Y per questa riga
            descCurrentY += lineSpacing;
            
            // Controlla se serve nuova pagina durante la descrizione
            if (descCurrentY > pageHeight - footerHeight - 2) {
              addFooter(pdf, descCurrentPage, 0);
              pdf.addPage();
              descCurrentPage++;
              pageNum = descCurrentPage;
              addHeader(pdf, companyData, quote?.quoteNumber, protocolNumber, pageNum);
              descCurrentY = addTableHeader(pdf, margin + 18);
              pdf.setFontSize(6.5);
              pdf.setFont(undefined, 'normal');
            }
            
            // Assicurati di essere sulla pagina corretta
            pdf.setPage(descCurrentPage);
            
            // Disegna la riga della descrizione
            pdf.text(line, margin + 22, descCurrentY);
          }
        });
        
        // Aggiorna currentY e pageNum per la prossima riga
        currentY = descCurrentY + lineSpacing;
        pageNum = descCurrentPage;
        
        // Quantità, Prezzo, Sconto, Totale (sulla prima riga della descrizione - itemStartY)
        // Torna alla pagina iniziale per disegnare i valori
        pdf.setPage(itemStartPage);
        pdf.text(String(item.quantity || 0), margin + 83, itemStartY, { align: 'center' });
        pdf.text(`€${parseFloat(item.unitPrice || 0).toFixed(2)}`, margin + 93, itemStartY, { align: 'right' });
        pdf.text(discountAmount > 0 ? `€${discountAmount.toFixed(2)}` : '-', margin + 106, itemStartY, { align: 'right' });
        pdf.text(`€${finalTotal.toFixed(2)}`, margin + 126, itemStartY, { align: 'right' });
        
        // Torna alla pagina corrente per continuare
        pdf.setPage(pageNum);
        pdf.setDrawColor(240, 240, 240);
        pdf.line(margin, currentY, pageWidth - margin, currentY);
        currentY += 1;
      }

      currentY += 4;

      // Totali
      if (currentY > pageHeight - footerHeight - 15) {
        addFooter(pdf, pageNum, 0);
        pdf.addPage();
        pageNum++;
        addHeader(pdf, companyData, quote?.quoteNumber, protocolNumber, pageNum);
        currentY = margin + 18;
      }

      pdf.setDrawColor(200, 200, 200);
      pdf.setLineWidth(0.3);
      pdf.line(margin, currentY, pageWidth - margin, currentY);
      currentY += 4;

      pdf.setFontSize(8);
      pdf.setFont(undefined, 'normal');
      pdf.text('Subtotale:', pageWidth - margin - 40, currentY, { align: 'right' });
      pdf.text(`€${(totals?.subtotal || 0).toFixed(2)}`, pageWidth - margin, currentY, { align: 'right' });
      currentY += 4;

      pdf.text(`IVA (${totals?.taxRate || 22}%) inclusa:`, pageWidth - margin - 40, currentY, { align: 'right' });
      pdf.text(`€${(totals?.taxAmount || 0).toFixed(2)}`, pageWidth - margin, currentY, { align: 'right' });
      currentY += 6;

      pdf.setDrawColor(0, 0, 0);
      pdf.setLineWidth(0.5);
      pdf.line(pageWidth - margin - 40, currentY, pageWidth - margin, currentY);
      currentY += 4;

      pdf.setFontSize(11);
      pdf.setTextColor(219, 17, 17);
      pdf.setFont(undefined, 'bold');
      pdf.text('TOTALE:', pageWidth - margin - 40, currentY, { align: 'right' });
      pdf.text(`€${(totals?.total || 0).toFixed(2)}`, pageWidth - margin, currentY, { align: 'right' });

      // Aggiungi footer all'ultima pagina
      const totalPages = pageNum;
      // Rimuovi il footer precedente se esiste e aggiungi quello corretto
      pdf.setPage(pageNum);
      addFooter(pdf, pageNum, totalPages);

      // Aggiungi footer a tutte le pagine precedenti
      for (let i = 1; i < pageNum; i++) {
        pdf.setPage(i);
        addFooter(pdf, i, totalPages);
      }

      // Torna all'ultima pagina
      pdf.setPage(pageNum);

      // Genera il PDF come blob e aprilo direttamente
      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `Preventivo_${quote?.quoteNumber || 'N/A'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(pdfUrl);
    } catch (error) {
      console.error('Errore durante la generazione del PDF preventivo:', error);
    }
  };

  const handlePrint = async (quote) => {
    try {
      // Recupera i dati completi del preventivo
      const response = await api.get(`/quotes/${quote.id}`);
      const fullQuote = response.data;
      
      // Prepara i dati per la generazione del PDF
      const quotePrintData = {
        quote: fullQuote,
        customer: fullQuote.customer || fullQuote.Customer,
        vehicle: fullQuote.vehicle || fullQuote.Vehicle,
        items: fullQuote.items || fullQuote.Items || [],
        maintenanceId: fullQuote.maintenanceId || maintenanceId,
        totals: calculateTotals(fullQuote.items || fullQuote.Items || [])
      };
      
      // Genera direttamente il PDF senza anteprima
      await generateQuotePdf(quotePrintData);
    } catch (error) {
      console.error('Errore nel recupero dati preventivo:', error);
    }
  };

  const handleImport = (quote) => {
    if (onImport) {
      onImport(quote);
    }
    onClose();
  };

  const handleEditQuote = (quote) => {
    setSelectedQuote(quote);
    setOpenEditDialog(true);
  };

  const handleQuoteSave = async (quoteData) => {
    try {
      // Se è una modifica (quote.esiste), aggiorna
      if (selectedQuote && selectedQuote.id) {
        await api.put(`/quotes/${selectedQuote.id}`, quoteData);
      } else {
        // Altrimenti crea nuovo preventivo
        await api.post('/quotes', quoteData);
      }
      
      setOpenEditDialog(false);
      setSelectedQuote(null);
      setQuotesReloadTrigger(prev => prev + 1); // Ricarica la lista
    } catch (error) {
      console.error('Errore nel salvataggio preventivo:', error);
    }
  };

  const calculateTotals = (items) => {
    const total = items.reduce((sum, item) => {
      const itemTotal = item.quantity * item.unitPrice;
      const discountAmount = parseFloat(item.discount) || 0; // Sconto in euro invece che percentuale
      return sum + (itemTotal - discountAmount);
    }, 0);

    const taxRate = 22;
    const subtotal = total / (1 + taxRate / 100);
    const taxAmount = total - subtotal;

    return {
      subtotal: parseFloat(subtotal.toFixed(2)),
      taxAmount: parseFloat(taxAmount.toFixed(2)),
      total: parseFloat(total.toFixed(2)),
      taxRate
    };
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>Preventivi Disponibili</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Preventivi Disponibili
        {quotes.length > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Trovati {quotes.length} preventivi per questa manutenzione
          </Typography>
        )}
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {quotes.length === 0 && !loading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              Nessun preventivo trovato per questa manutenzione
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Crea un nuovo preventivo per poterlo visualizzare qui
            </Typography>
          </Box>
        )}

        {quotes.map((quote) => {
          const totals = calculateTotals(quote.items || []);
          
          return (
            <Paper key={quote.id} sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {quote.quoteNumber}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Cliente: {quote.customer?.firstName} {quote.customer?.lastName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Veicolo: {quote.vehicle?.brand} {quote.vehicle?.model}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Data: {format(new Date(quote.createdAt), 'dd/MM/yyyy HH:mm', { locale: it })}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip 
                    label={`€${totals.total.toFixed(2)}`} 
                    color="primary" 
                    variant="outlined"
                  />
                  <Chip 
                    label={`${quote.items?.length || 0} articoli`} 
                    color="secondary" 
                    variant="outlined"
                  />
                </Box>
              </Box>

              {quote.items && quote.items.length > 0 && (
                <TableContainer sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Prodotto</TableCell>
                        <TableCell align="center">Quantità</TableCell>
                        <TableCell align="right">Prezzo Unit.</TableCell>
                        <TableCell align="right">Sconto</TableCell>
                        <TableCell align="right">Totale</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {quote.items.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {item.productName}
                            </Typography>
                            {item.productCode && (
                              <Typography variant="caption" color="text.secondary">
                                Codice: {item.productCode}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">{item.quantity}</TableCell>
                          <TableCell align="right">€{item.unitPrice ? parseFloat(item.unitPrice).toFixed(2) : '0.00'}</TableCell>
                          <TableCell align="right">€{parseFloat(item.discount || 0).toFixed(2)}</TableCell>
                          <TableCell align="right">
                            €{(((item.quantity || 0) * (parseFloat(item.unitPrice) || 0)) - (parseFloat(item.discount) || 0)).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => handleEditQuote(quote)}
                    size="small"
                    color="secondary"
                  >
                    Modifica
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<PrintIcon />}
                    onClick={() => handlePrint(quote)}
                    size="small"
                  >
                    Stampa
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<ImportIcon />}
                    onClick={() => handleImport(quote)}
                    size="small"
                    color="primary"
                  >
                    Importa
                  </Button>
                </Box>
                
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" color="text.secondary">
                    Subtotale: €{totals.subtotal.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    IVA ({totals.taxRate}%) inclusa: €{totals.taxAmount.toFixed(2)}
                  </Typography>
                  <Typography variant="h6" color="primary">
                    Totale: €{totals.total.toFixed(2)}
                  </Typography>
                </Box>
              </Box>

              {/* Componente di stampa nascosto */}
              <Box sx={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
                <div id={`quote-print-${quote.id}`}>
                  <QuotePrint
                    quote={quote}
                    customer={quote.customer}
                    vehicle={quote.vehicle}
                    items={quote.items || []}
                    totals={totals}
                  />
                </div>
              </Box>
            </Paper>
          );
        })}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} startIcon={<CloseIcon />}>
          Chiudi
        </Button>
      </DialogActions>

      {/* Dialog per modifica preventivo */}
      <QuoteDialog
        open={openEditDialog}
        onClose={() => {
          setOpenEditDialog(false);
          setSelectedQuote(null);
        }}
        onSave={handleQuoteSave}
        customer={selectedQuote?.customer || undefined}
        vehicle={selectedQuote?.vehicle || undefined}
        maintenanceData={selectedQuote ? {
          protocolNumber: selectedQuote.maintenance?.protocolNumber || '',
          priority: 'Normale'
        } : undefined}
        editMode={true}
        initialData={selectedQuote}
      />
    </Dialog>
  );
};

export default QuoteViewDialog;
